'use client';

import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useEnvironmentStore } from '@/lib/environments/store';
import type { Environment, CreateEnvironmentInput, UpdateEnvironmentInput } from '@/lib/environments/types';
import { SecretsManager } from './secrets-manager';
import { Settings2, Key, Trash2, AlertTriangle } from 'lucide-react';

interface EnvironmentSettingsProps {
  environment: Environment | null;
  open: boolean;
  onClose: () => void;
}

const COLORS = [
  '#10B981', // Green
  '#3B82F6', // Blue
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#14B8A6', // Teal
];

export function EnvironmentSettings({ environment, open, onClose }: EnvironmentSettingsProps) {
  const {
    environments,
    environmentsLoading,
    environmentsError,
    createEnvironment,
    updateEnvironment,
    deleteEnvironment,
    clearErrors,
  } = useEnvironmentStore();

  const isEditing = !!environment;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    reset,
  } = useForm<CreateEnvironmentInput | UpdateEnvironmentInput>({
    defaultValues: environment ? {
      display_name: environment.display_name || '',
      description: environment.description || '',
      is_protected: environment.is_protected,
      color: environment.color,
      can_promote_to: environment.can_promote_to,
      requires_approval: environment.requires_approval,
      approval_roles: environment.approval_roles,
    } : {
      name: '',
      display_name: '',
      description: '',
      type: 'development',
      is_protected: false,
      color: COLORS[0],
      can_promote_to: [],
      requires_approval: false,
      approval_roles: ['admin'],
    },
  });

  const onSubmit = async (data: CreateEnvironmentInput | UpdateEnvironmentInput) => {
    try {
      if (isEditing) {
        await updateEnvironment(environment.id, data as UpdateEnvironmentInput);
      } else {
        await createEnvironment(data as CreateEnvironmentInput);
      }
      onClose();
    } catch (error) {
      // Error handled by store
    }
  };

  const handleDelete = async () => {
    if (!environment) return;
    if (confirm(`Are you sure you want to delete "${environment.display_name || environment.name}"? This cannot be undone.`)) {
      try {
        await deleteEnvironment(environment.id);
        onClose();
      } catch (error) {
        // Error handled by store
      }
    }
  };

  const handleClose = () => {
    clearErrors();
    reset();
    onClose();
  };

  const selectedColor = watch('color');
  const otherEnvironments = environments.filter((e) => e.id !== environment?.id);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Environment' : 'Create Environment'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update environment settings and configuration'
              : 'Create a new deployment environment'}
          </DialogDescription>
        </DialogHeader>

        {environmentsError && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>{environmentsError}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="settings">
          <TabsList>
            <TabsTrigger value="settings">
              <Settings2 className="size-4 mr-2" />
              Settings
            </TabsTrigger>
            {isEditing && (
              <TabsTrigger value="secrets">
                <Key className="size-4 mr-2" />
                Secrets
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="settings" className="mt-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                {!isEditing && (
                  <div>
                    <Label htmlFor="name">
                      Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      {...register('name' as any, { required: 'Name is required' })}
                      placeholder="production-us-east"
                      className="mt-1"
                    />
                    {(errors as any).name && (
                      <p className="text-sm text-destructive mt-1">
                        {(errors as any).name.message}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      Unique identifier (lowercase, no spaces)
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    {...register('display_name')}
                    placeholder="Production (US East)"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="Main production environment for US East region"
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {!isEditing && (
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select
                      value={watch('type' as any)}
                      onValueChange={(value) => setValue('type' as any, value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="development">Development</SelectItem>
                        <SelectItem value="staging">Staging</SelectItem>
                        <SelectItem value="production">Production</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Color</Label>
                  <div className="flex items-center gap-2 mt-2">
                    {COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`size-8 rounded-full border-2 transition-all ${
                          selectedColor === color
                            ? 'border-primary scale-110'
                            : 'border-transparent hover:border-muted-foreground/50'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setValue('color', color)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Protection Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">Protection Settings</h4>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Protected Environment</Label>
                    <p className="text-sm text-muted-foreground">
                      Require approval for deployments
                    </p>
                  </div>
                  <Switch
                    checked={watch('is_protected')}
                    onCheckedChange={(checked) => setValue('is_protected', checked)}
                  />
                </div>

                {watch('is_protected') && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Require Approval</Label>
                        <p className="text-sm text-muted-foreground">
                          Deployments must be approved before activation
                        </p>
                      </div>
                      <Switch
                        checked={watch('requires_approval')}
                        onCheckedChange={(checked) => setValue('requires_approval', checked)}
                      />
                    </div>

                    {watch('requires_approval') && (
                      <div>
                        <Label>Approval Roles</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {['admin', 'lead', 'deployer'].map((role) => (
                            <Badge
                              key={role}
                              variant={
                                (watch('approval_roles') || []).includes(role)
                                  ? 'default'
                                  : 'outline'
                              }
                              className="cursor-pointer capitalize"
                              onClick={() => {
                                const current = watch('approval_roles') || [];
                                if (current.includes(role)) {
                                  setValue(
                                    'approval_roles',
                                    current.filter((r) => r !== role)
                                  );
                                } else {
                                  setValue('approval_roles', [...current, role]);
                                }
                              }}
                            >
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Promotion Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">Promotion Settings</h4>
                <p className="text-sm text-muted-foreground">
                  Select environments this one can promote deployments to
                </p>

                <div className="flex flex-wrap gap-2">
                  {otherEnvironments.map((env) => (
                    <Badge
                      key={env.id}
                      variant={
                        (watch('can_promote_to') || []).includes(env.id)
                          ? 'default'
                          : 'outline'
                      }
                      className="cursor-pointer"
                      style={{
                        borderColor: env.color,
                        backgroundColor: (watch('can_promote_to') || []).includes(env.id)
                          ? env.color
                          : 'transparent',
                      }}
                      onClick={() => {
                        const current = watch('can_promote_to') || [];
                        if (current.includes(env.id)) {
                          setValue(
                            'can_promote_to',
                            current.filter((id) => id !== env.id)
                          );
                        } else {
                          setValue('can_promote_to', [...current, env.id]);
                        }
                      }}
                    >
                      {env.display_name || env.name}
                    </Badge>
                  ))}
                  {otherEnvironments.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No other environments available
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                {isEditing && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={environmentsLoading}
                  >
                    <Trash2 className="size-4 mr-2" />
                    Delete
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={environmentsLoading || (!isDirty && isEditing)}>
                  {environmentsLoading
                    ? 'Saving...'
                    : isEditing
                    ? 'Update Environment'
                    : 'Create Environment'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          {isEditing && (
            <TabsContent value="secrets" className="mt-4">
              <SecretsManager environmentId={environment.id} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
